/**
 * ItemRow — Plan 04-03 / UI-SPEC §"<ItemRow />".
 *
 * Single horizontal row, ≥56px tall (phone) / ≥64px (tablet). Layout:
 *   [☐ checkbox] [item text]  [attribution chip] [⋯ override trigger]
 *
 * Behaviour contract (locked by UI-SPEC §"Interaction Model"):
 *   - Whole-row tap toggles `checked` (D-07 open-write — anyone can check
 *     anyone's item). 200ms tap-debounce prevents double-tap dupes.
 *   - Long-press ≥600ms with ≤8px finger drift OR ⋯-trigger tap calls
 *     `props.onOpenOverride(item)`. Both gestures share a SINGLE prop —
 *     ItemRow itself holds NO sheet state. Plan 05 wires CategoryOverrideSheet
 *     by reading from the parent's state without touching this component.
 *   - `pointercancel` / `pointerleave` aborts the long-press timer.
 *
 * Props contract:
 *   - `item: ShoppingItem`         — the row data (server canonical row)
 *   - `onOpenOverride(item)`       — REQUIRED single channel for both
 *                                    gestures (long-press + ⋯ trigger)
 *   - `pending?: boolean`          — show OfflinePendingChip inline
 *   - `addedByMember?: {short_name, color_token}` — drives attribution chip
 *
 * Aria: `role="checkbox" aria-checked={item.checked === 1}` står på boksen
 * og teksten (se #157 under). Boksen som vises, er pynt.
 *
 * T-04-05: item text rendered via plain JSX text interpolation
 * (`<span>{item.text}</span>`); ZERO `dangerouslySetInnerHTML`.
 *
 * #157 (redesign steg 7d, design/screens/Stell_Handle.dc.html): raden er
 * skissens — 30 px avkrysningsboks (lime når avkrysset), varen i 15 px / 700
 * og «Lagt til av …» under, nå også på telefon, og ⋯ på 44 px ytterst.
 * Oppførselen er uendret: boksen og teksten krysser av, og ⋯ (eller langt
 * trykk) åpner kategoriarket. Redigering og sletting er ny oppførsel 6.
 *
 * ⋯ står nå ved siden av avkrysningen og ikke inni den. Før lå knappen inne
 * i `role="checkbox"`, og axe meldte `nested-interactive` (alvorlig) for
 * hver rad. `shopping-item` med `aria-checked` er avkrysningen; navnet er
 * varen, og «Lagt til av …» og «Venter på nett» er beskrivelsen.
 */
import {
  useCallback,
  useId,
  useRef,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ShoppingItem } from '@stell/shared';
import { Icon } from '../../components/primitives/Icon';
import type { ColorSlot } from '../../components/primitives/PixelAvatar';
import { OfflinePendingChip } from './OfflinePendingChip';
import { useShoppingMutations } from './useShoppingMutations';

const LONG_PRESS_MS = 600;
const LONG_PRESS_DRIFT_PX = 8;

interface AddedByMember {
  short_name: string;
  color_token: ColorSlot;
}

interface ItemRowProps {
  item: ShoppingItem;
  /** Single channel for long-press + ⋯ trigger gestures (UI-SPEC §Interaction Model). */
  onOpenOverride: (item: ShoppingItem) => void;
  pending?: boolean | undefined;
  addedByMember?: AddedByMember | undefined;
}

export function ItemRow({
  item,
  onOpenOverride,
  pending,
  addedByMember,
}: ItemRowProps) {
  const { t } = useTranslation();
  const { checkItem } = useShoppingMutations();
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef<boolean>(false);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef<number>(0);
  const textId = useId();
  const byId = useId();
  const pendingId = useId();
  // «Lagt til av …» og «Venter på nett» er beskrivelsen; navnet er varen.
  const describedBy =
    [addedByMember ? byId : '', pending ? pendingId : ''].filter(Boolean).join(' ') || undefined;

  const checked = item.checked === 1;

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    startPos.current = null;
  }, []);

  const onPointerDown = useCallback(
    (ev: PointerEvent<HTMLDivElement>) => {
      longPressFired.current = false;
      startPos.current = { x: ev.clientX, y: ev.clientY };
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        onOpenOverride(item);
      }, LONG_PRESS_MS);
    },
    [item, onOpenOverride],
  );

  const onPointerMove = useCallback(
    (ev: PointerEvent<HTMLDivElement>) => {
      if (!startPos.current) return;
      const dx = ev.clientX - startPos.current.x;
      const dy = ev.clientY - startPos.current.y;
      if (Math.hypot(dx, dy) > LONG_PRESS_DRIFT_PX) cancelLongPress();
    },
    [cancelLongPress],
  );

  const onPointerUp = useCallback(() => {
    cancelLongPress();
  }, [cancelLongPress]);

  const onPointerCancel = useCallback(() => {
    cancelLongPress();
  }, [cancelLongPress]);

  const toggleChecked = useCallback(() => {
    if (longPressFired.current) {
      // Long-press already opened the override sheet — don't also toggle.
      longPressFired.current = false;
      return;
    }
    const now = Date.now();
    if (now - lastTap.current < 200) return; // 200ms tap-debounce
    lastTap.current = now;
    checkItem.mutate(item);
  }, [checkItem, item]);

  const onKeyDown = useCallback(
    (ev: KeyboardEvent<HTMLDivElement>) => {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        toggleChecked();
      }
    },
    [toggleChecked],
  );

  const onOverrideClick = useCallback(() => onOpenOverride(item), [item, onOpenOverride]);

  return (
    <div className="flex min-h-13 items-center gap-1">
      <div
        role="checkbox"
        aria-checked={checked}
        aria-labelledby={textId}
        aria-describedby={describedBy}
        tabIndex={0}
        data-testid="shopping-item"
        data-item-id={item.id}
        onClick={toggleChecked}
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerCancel}
        className="flex min-h-13 min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-md py-1 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
      >
        {/* Checkbox glyph — decorative; semantics live on the row itself. */}
        <span
          aria-hidden
          data-testid={`shopping-item-checkbox-${item.id}`}
          className={[
            'flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-sm',
            checked ? 'bg-fab text-on-fab' : 'border-2 border-secondary-line text-transparent',
          ].join(' ')}
        >
          {checked ? <Icon.Check size={16} /> : null}
        </span>
        <span className="min-w-0 flex-1">
          <span
            id={textId}
            data-testid={`shopping-item-text-${item.id}`}
            className={[
              'block text-body font-bold break-words',
              // Avkrysset er gjennomstreket i ink-2, ikke halvgjennomsiktig, så
              // teksten holder 4.5:1.
              checked ? 'line-through text-ink-2' : 'text-ink',
            ].join(' ')}
          >
            {item.text}
          </span>
          {addedByMember ? (
            <span
              id={byId}
              data-testid={`shopping-item-attribution-${item.id}`}
              className="block text-caption text-ink-2"
            >
              {t('shopping.item.attribution', { nick: addedByMember.short_name })}
            </span>
          ) : null}
        </span>
        {/* Offline-pending chip (when applicable) */}
        {pending ? (
          <span id={pendingId} data-testid={`shopping-item-pending-${item.id}`} className="shrink-0">
            <OfflinePendingChip />
          </span>
        ) : null}
      </div>
      {/* ⋯ override trigger (44×44 right-edge) */}
      <button
        type="button"
        data-testid={`shopping-item-override-${item.id}`}
        aria-label={t('shopping.item.override_aria')}
        onClick={onOverrideClick}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:text-ink"
      >
        <svg
          aria-hidden
          xmlns="http://www.w3.org/2000/svg"
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <circle cx="6" cy="12" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="18" cy="12" r="1.5" />
        </svg>
      </button>
    </div>
  );
}
